import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ROUND_ONE_CONFIRM = 'DELETE ROUND 1';
const normalizeConfirmText = (value = '') => String(value).trim().toUpperCase();

const DeleteIterationDialog = ({ pending, onCancel, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const requiresTypedConfirm = pending?.iteration?.number === 1;

  useEffect(() => {
    if (!pending) setConfirmText('');
  }, [pending]);

  const canConfirm = !requiresTypedConfirm || normalizeConfirmText(confirmText) === ROUND_ONE_CONFIRM;

  return (
    <Dialog
      open={Boolean(pending)}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <Box sx={{ height: 4, bgcolor: 'error.main' }} />
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: 2.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </Box>
        <span>Delete Round {pending?.iteration?.number}?</span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography variant="body2" sx={{ mb: 1 }}>
            You're about to delete <strong>{pending?.iteration?.label}</strong>{' '}
            created {pending?.iteration?.createdAt
              ? new Date(pending.iteration.createdAt).toLocaleDateString()
              : '—'}.
          </Typography>
          {pending?.summary && (
            <Box
              sx={{
                my: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'error.main',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
                This will permanently remove:
              </Typography>
              <Stack spacing={0.25} sx={{ pl: 0.5 }}>
                <Typography variant="caption">
                  · {pending.summary.reviewedCount} signed-off review
                  {pending.summary.reviewedCount === 1 ? '' : 's'}
                </Typography>
                <Typography variant="caption">
                  · {pending.summary.openCount} open variance
                  {pending.summary.openCount === 1 ? '' : 's'} tracked in this round
                </Typography>
                <Typography variant="caption">
                  · All investigation notes and category/remediation tags attached to this round
                </Typography>
              </Stack>
            </Box>
          )}
          {requiresTypedConfirm && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={`Type ${ROUND_ONE_CONFIRM} to confirm`}
              helperText="Capitalization does not matter."
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              sx={{ my: 1.5 }}
            />
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Other rounds for this month are not affected. If this is the only round, a fresh
            Round 1 will be created automatically.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">Cancel</Button>
        <Button
          onClick={() => onConfirm(confirmText)}
          color="error"
          variant="contained"
          startIcon={<DeleteOutlineIcon />}
          disabled={!canConfirm}
        >
          Delete round
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteIterationDialog;
